package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.remember
import com.milta.core.localization.LocalLanguageTag
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.ProvideAppStrings
import com.milta.core.localization.supportedLanguages
import com.milta.di.PUBLIC_LEGAL_DOCUMENTS
import com.milta.feature.legal.domain.entity.LegalDocumentType
import com.milta.feature.legal.domain.usecase.GetLegalDocumentUseCase
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.koin.compose.koinInject
import org.koin.core.qualifier.named
import org.w3c.dom.url.URLSearchParams

/**
 * Public pages reachable without signing in — required by the Google Play listing (privacy policy,
 * terms of use, account deletion). Picked by [main] from `window.location.pathname` BEFORE the
 * dashboard [App] is ever composed, so none of its session logic (token check, role gate,
 * `sessionEvents` redirect to Welcome) runs: a signed-in user opening `/privacy` sees this same
 * public page, not the dashboard. Links between these pages are plain `<a href>` (full page load),
 * no pushState routing to keep in sync.
 */
internal enum class PublicRoute {
    PRIVACY,
    TERMS,
    DELETE_ACCOUNT;

    companion object {
        fun parse(pathname: String): PublicRoute? = when (pathname.trimEnd('/')) {
            "/privacy" -> PRIVACY
            "/terms" -> TERMS
            "/delete-account" -> DELETE_ACCOUNT
            else -> null
        }
    }
}

private const val CONTACT_EMAIL = "contact@milta.pro"

/** French is the reference language of these pages, unlike [com.milta.core.localization.resolveLanguageTag]'s `en`. */
private const val PUBLIC_DEFAULT_LANGUAGE = "fr"

/** `?lang=xx` wins over the browser language (`navigator.language`, e.g. "nl-BE" → "nl"); anything
 * unsupported falls back to French. */
private fun publicLanguageTag(): String {
    val candidates = listOfNotNull(
        URLSearchParams(window.location.search).get("lang"),
        window.navigator.language,
    )
    return candidates
        .map { it.substringBefore('-').lowercase() }
        .firstOrNull { it in supportedLanguages }
        ?: PUBLIC_DEFAULT_LANGUAGE
}

@Composable
internal fun PublicPage(route: PublicRoute) {
    val language = remember { publicLanguageTag() }
    SideEffect { document.documentElement?.setAttribute("lang", language) }
    // Built on the anonymous HTTP client, never the authenticated one (see PUBLIC_LEGAL_DOCUMENTS).
    val getLegalDocument: GetLegalDocumentUseCase = koinInject(qualifier = named(PUBLIC_LEGAL_DOCUMENTS))

    ProvideAppStrings(languageTag = language) {
        Div(
            attrs = {
                style {
                    property("min-height", "100vh"); property("background", WebSurfaceDim)
                    property("padding", "32px 16px 48px"); property("box-sizing", "border-box")
                }
            },
        ) {
            Div(attrs = { style { property("max-width", "760px"); property("margin", "0 auto"); property("display", "flex"); property("flex-direction", "column"); property("gap", "24px") } }) {
                Img(
                    src = drawableUri("milta_full_logo.png"),
                    alt = LocalStrings.current.common.appName,
                    attrs = { style { property("height", "40px"); property("width", "fit-content") } },
                )
                when (route) {
                    PublicRoute.PRIVACY, PublicRoute.TERMS -> {
                        // Browser tab: "<API title> — Milta" once loaded, plain "Milta" before or on error.
                        val appName = LocalStrings.current.common.appName
                        WebLegalContent(
                            type = if (route == PublicRoute.PRIVACY) LegalDocumentType.PrivacyPolicy else LegalDocumentType.TermsOfUse,
                            onBack = null,
                            getLegalDocument = getLegalDocument,
                            preserveLineBreaks = true,
                            onDocumentTitle = { title -> document.title = if (title != null) "$title — $appName" else appName },
                        )
                    }
                    PublicRoute.DELETE_ACCOUNT -> DeleteAccountContent()
                }
            }
        }
    }
}

/** Static `/delete-account` page — text from [com.milta.core.localization.PublicPagesStrings]. */
@Composable
private fun DeleteAccountContent() {
    val strings = LocalStrings.current.publicPages
    // The page title already names Milta: no " — Milta" suffix on this tab.
    SideEffect { document.title = strings.deleteAccountTitle }

    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "12px")
                property("padding", "28px"); property("color", WebTextPrimary); property("font-size", "14px"); property("line-height", "1.6")
            }
        },
    ) {
        H1(attrs = { style { property("font-size", "22px"); property("margin", "0 0 12px") } }) { Text(strings.deleteAccountTitle) }
        P { Text(strings.deleteAccountIntro) }

        SectionTitle(strings.deleteAccountInAppTitle)
        Ol { strings.deleteAccountInAppSteps.forEach { Li { Text(it) } } }
        P { Text(strings.deleteAccountEnterpriseNote) }

        SectionTitle(strings.deleteAccountNoAppTitle)
        P {
            val (before, after) = strings.deleteAccountNoAppText.split("%s", limit = 2).let { it[0] to it.getOrElse(1) { "" } }
            Text(before)
            A(href = "mailto:$CONTACT_EMAIL", attrs = { style { property("color", WebPrimary); property("user-select", "text") } }) { Text(CONTACT_EMAIL) }
            Text(after)
        }

        SectionTitle(strings.deleteAccountDeletedTitle)
        Ul { strings.deleteAccountDeletedItems.forEach { Li { Text(it) } } }

        SectionTitle(strings.deleteAccountKeptTitle)
        Ul { strings.deleteAccountKeptItems.forEach { Li { Text(it) } } }

        SectionTitle(strings.deleteOrganizationTitle)
        P { Text(strings.deleteOrganizationText) }

        Div(attrs = { style { property("margin-top", "24px"); property("padding-top", "16px"); property("border-top", "1px solid $WebBorder") } }) {
            // Keeps the language of this page, whatever the browser language.
            A(href = "/privacy?lang=${LocalLanguageTag.current}", attrs = { style { property("color", WebPrimary); property("font-weight", "600") } }) { Text(strings.privacyPolicyLink) }
        }
    }
}

@Composable
private fun SectionTitle(text: String) {
    H2(attrs = { style { property("font-size", "16px"); property("margin", "24px 0 8px") } }) { Text(text) }
}
